/**
 * API Client — axios instance with CSRF, snake_case params, and error transformation.
 *
 * @example
 * import { apiClient } from './client';
 * const { data } = await apiClient.get('/users', { params: { status: 'active' } });
 */

import axios, { type AxiosError } from 'axios';

// ─── Error class ─────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string,
    public data?: unknown,
    public serverMessage?: string,
    public fieldErrors?: Record<string, string>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const SPINE_LINK_BLOCKED_CODE = 'spine_link_blocked';

function isSpineLinkError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.fieldErrors?.code === SPINE_LINK_BLOCKED_CODE;
}

/** Extract field errors from a caught error */
export function getFieldErrors(error: unknown): Record<string, string> | undefined {
  return error instanceof ApiError ? error.fieldErrors : undefined;
}

/** Get the error message for a specific field */
export function getFieldError(error: unknown, field: string): string | undefined {
  return getFieldErrors(error)?.[field];
}

/**
 * Best user-facing description for a toast: a `base` (non-field) 422 reason
 * when present — e.g. "Cannot confirm order with draft events" — since that's
 * more actionable than the generic "Some fields are not right" every 422
 * carries as `error.message`. Falls back to the error's own message, then the
 * caller-supplied fallback.
 */
export function getErrorDescription(error: unknown, fallback?: string): string {
  const base = getFieldErrors(error)?.base;
  if (base) return base;
  if (error instanceof Error && error.message) return error.message;
  return fallback ?? '';
}

/**
 * Every 422 reason, one per line. For actions blocked by a small, fixed set of
 * guards, which report on named fields as well as `base`.
 *
 * Not for form saves: those 422s carry every validation error on the record,
 * which belong inline on the fields. Use `getErrorDescription` there.
 */
export function getAllErrorReasons(error: unknown, fallback?: string): string {
  if (isSpineLinkError(error)) {
    return error.serverMessage || fallback || '';
  }

  const reasons = Object.values(getFieldErrors(error) ?? {});
  if (reasons.length) return reasons.join('\n');
  if (error instanceof Error && error.message) return error.message;
  return fallback ?? '';
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getCsrfToken(): string | null {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? null;
}

// axios sets one of these on `error.code` when a request times out, rather than
// returning an HTTP response. A timeout reads differently from a plain drop.
const TIMEOUT_CODES = ['ECONNABORTED', 'ETIMEDOUT'];

// The request never reached an HTTP response (status 0): a network drop, a
// timeout, an aborted request, or a proxy rejection. Name the cause so a repeat
// is diagnosable instead of hiding behind the generic default.
function getNoResponseMessage(code?: string): string {
  if (code && TIMEOUT_CODES.includes(code)) {
    return 'The request timed out. Please check your connection and try again.';
  }
  return 'Could not reach the server. Please check your connection and try again.';
}

export function getErrorMessage(status: number, statusText: string, code?: string): string {
  switch (status) {
    case 0: return getNoResponseMessage(code);
    case 401: return 'Please log in to continue.';
    case 403: return 'You don\'t have permission to do that.';
    case 404: return 'Resource not found.';
    case 422: return 'Invalid request. Please check your input.';
    case 429: return 'Too many requests. Please wait a moment.';
    case 500: case 502: case 503: return 'Server error. Please try again later.';
    default: return statusText || 'Something went wrong.';
  }
}

/** Convert params object to URLSearchParams, camelCase keys → snake_case */
function serializeParams(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
    if (Array.isArray(value)) {
      value.forEach(item => searchParams.append(`${snakeKey}[]`, String(item)));
    } else {
      searchParams.set(snakeKey, String(value));
    }
  }
  return searchParams.toString();
}

// ─── Axios instance ──────────────────────────────────────────────────────────

export const apiClient = axios.create({
  // Bound every request so a stalled connection — e.g. a multipart image upload
  // on a dropped network — fails with a named timeout instead of hanging
  // forever. Generous enough for normal uploads on a working connection.
  timeout: 60_000,
  headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
  withCredentials: true,
  paramsSerializer: { serialize: serializeParams },
});

apiClient.interceptors.request.use((config) => {
  const token = getCsrfToken();
  if (token) config.headers['X-CSRF-Token'] = token;
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ message?: string; errors?: string[] | Record<string, string[] | string>; data?: Record<string, string> }>) => {
    const status = error.response?.status ?? 0;
    const statusText = error.response?.statusText ?? '';
    const body = error.response?.data;
    const errorsMessage = formatErrors(body?.errors);
    const serverMessage = body?.message || errorsMessage;
    const message = serverMessage || getErrorMessage(status, statusText, error.code);

    throw new ApiError(message, status, statusText, body, serverMessage, body?.data);
  }
);

// A 422 from this API always carries a reason the user can act on, so callers
// may show it verbatim. Keep that true of every 422 they can trigger.
export function actionable422Message(error: unknown): string | undefined {
  return error instanceof ApiError && error.status === 422 ? error.serverMessage : undefined;
}

function formatErrors(errors: string[] | Record<string, string[] | string> | undefined): string | undefined {
  if (!errors) return undefined;
  if (Array.isArray(errors)) return errors.join(', ');
  // Rails ActiveRecord returns `{field: ["msg1", "msg2"]}` from `errors.messages`.
  const flat = Object.values(errors).flatMap((v) => (Array.isArray(v) ? v : [v]));
  return flat.length > 0 ? flat.join(', ') : undefined;
}
